import {
  ApplicationSource,
  ApplicationStatusFolder,
  type CredentialInfo as CredentialInfoHirerType,
} from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import {
  type RoleRequirementStatusType,
  STATUS_CUSTOM,
} from 'src/constants/roleRequirementStatus';
import type { ApplicationFragment } from 'src/graphql/application.hirer.graphql.generated';
import type { ApplicationQuestionnaireQuestion } from 'src/graphql/types';
import { mapGraphAttachmentTypeToStore } from 'src/stores/JobApplicationStore/utils/mapGraphAttachmentTypeToStore';
import { JOB_APPLICATION_STATES } from 'src/stores/state';

import AttachmentsStore from '../AttachmentsStore';
import CandidateStore from '../CandidateStore';
import type JobApplication from '../JobApplication';
import { MatchedQualities } from '../MatchedQuality/MatchedQuality';
import Notes from '../Notes';
import ProfileStore, {
  Education,
  Licence,
  Skill,
  WorkHistory,
  RightToWork,
  RightToWorkV2,
  Nationality,
  CredentialInfo,
  EducationCompletionDate,
  ProfileTextObject,
  MonthYear,
  BaseCredential,
} from '../ProfileStore';
import ProspectEmailStore from '../ProspectEmailStore';
import RoleReqAnswer from '../RoleReqAnswer';
import RoleRequirement from '../RoleRequirement';
import {
  type IVerification,
  Verification,
} from '../VerificationStore/Verification';
import { BUCKETS } from '../types';

type JobApplicationType = NonNullable<
  Parameters<typeof JobApplication.create>[0]
>;

export const mapApplicationResponseToStore = (
  application: ApplicationFragment,
  positionInList?: number,
  selected?: boolean,
): JobApplicationType => ({
  // JobApplicationSummary
  id: application.adcentreProspectId,
  applicationCorrelationId: application.id,
  firstName: application.firstName,
  lastName: application.lastName,
  isRead: application.isRead,
  // JobApplicationDetails
  candidateId: application.adcentreCandidateId,
  billableStatus: mapToBillableStatus(application.billableStatus),
  seekCandidateId: application.candidateId ?? '',
  mostRecentJobTitle: application.mostRecentJobTitle ?? '',
  mostRecentCompanyName: application.mostRecentCompanyName ?? '',
  mostRecentRoleMonths: application.mostRecentRoleMonths,
  appliedDate: application.appliedDateUtc,
  email: application.email ?? '',
  phone: application.phone,
  bucket: mapToBucket(application.statusFolder),
  source: application.source === ApplicationSource.NATIVE ? 1 : 2,
  jobApplicationId: application.applicationId ?? '',
  ...(application.rating
    ? { rating: { rating: application.rating.rating } }
    : {}),
  ...mapToMetadata(application.metadata, application.candidateStats),
  ...mapToAttachments(application.attachmentsV2),
  matchedQualities: MatchedQualities.create({
    hasError: false,
    qualities: application.matchedQualities.map((quality) => ({
      id: quality.id,
      isTop: quality.isTop,
      displayLabel: quality.displayLabel,
      relevanceScore: quality.relevanceScore,
    })),
  }),
  verifications: mapToVerifications(application.verifications),
  verificationsCount: mapToVerificationsCount(application.profile),
  ...mapToRoleRequirements(application.questionnaireSubmission),
  profile: mapToProfile(application.profile),
  state: JOB_APPLICATION_STATES.STATE_LOADED,
  positionInList,
  selected,
});

const mapToProfile = (profile: ApplicationFragment['profile']) => {
  if (profile.__typename !== 'ApplicationProfileResponseSuccess') {
    return null;
  }

  if (!profile.result) {
    return null;
  }

  return ProfileStore.create({
    education: mapToEducation(profile),
    licences: mapToLicences(profile),
    skills: mapToSkills(profile),
    workHistory: mapToWorkHistory(profile),
    rightsToWork: mapToRightsToWork(profile),
    rightsToWorkV2: mapToRightsToWorkV2(profile),
    verifiedIdentity: profile.result.verifiedIdentity
      ? {
          fullName: profile.result.verifiedIdentity.fullName,
        }
      : profile.result.verifiedIdentity,
    homeLocation: profile.result.homeLocation
      ? {
          id: profile.result.homeLocation.id,
          description: profile.result.homeLocation.displayDescription,
        }
      : profile.result.homeLocation,
    nationality: mapToNationality(profile),
  });
};

const mapToNationality = (profile: ApplicationFragment['profile']) => {
  if (
    profile.__typename !== 'ApplicationProfileResponseSuccess' ||
    profile.result?.nationalities.__typename !==
      'ApplicationProfileNationalitiesResponseSuccess'
  ) {
    return [];
  }

  return profile.result.nationalities.result.map((nationality) =>
    Nationality.create({
      id: nationality.id,
      countryName: nationality.countryName,
    }),
  );
};

const mapToRightsToWorkV2 = (profile: ApplicationFragment['profile']) => {
  if (
    profile.__typename !== 'ApplicationProfileResponseSuccess' ||
    !profile.result
  ) {
    return [];
  }

  return profile.result.rightsToWorkV2.map((rightToWorkV2) =>
    RightToWorkV2.create({
      id: rightToWorkV2.id,
      displayLabel: rightToWorkV2.displayLabel,
    }),
  );
};

const mapToRightsToWork = (profile: ApplicationFragment['profile']) => {
  if (
    profile.__typename !== 'ApplicationProfileResponseSuccess' ||
    !profile.result
  ) {
    return [];
  }

  return profile.result.rightsToWork.map((rightToWork) =>
    RightToWork.create({
      answer: rightToWork.answer,
      country: rightToWork.country,
    }),
  );
};

const mapToWorkHistory = (profile: ApplicationFragment['profile']) => {
  if (
    profile.__typename !== 'ApplicationProfileResponseSuccess' ||
    !profile.result
  ) {
    return [];
  }

  return profile.result.workHistory.map((workHistory) =>
    WorkHistory.create({
      id: workHistory.id,
      title: { text: workHistory.title },
      company: { text: workHistory.company },
      startDate: {
        month: workHistory.startDate.month,
        year: workHistory.startDate.year,
      },
      endDate: workHistory.endDate
        ? { month: workHistory.endDate.month, year: workHistory.endDate.year }
        : null,
      achievements: workHistory.achievements,
    }),
  );
};

const mapToSkills = (profile: ApplicationFragment['profile']) => {
  if (
    profile.__typename !== 'ApplicationProfileResponseSuccess' ||
    !profile.result
  ) {
    return [];
  }

  return profile.result.skills.map((skill) =>
    Skill.create({
      keyword: { text: skill.keyword },
    }),
  );
};

const mapToCredential = (credential: CredentialInfoHirerType[]) => {
  if (credential.length === 0) {
    return null;
  }

  return BaseCredential.create({
    credentialInfo: credential.map(({ name, values }) =>
      CredentialInfo.create({
        name,
        values,
      }),
    ),
  });
};

const mapToLicences = (profile: ApplicationFragment['profile']) => {
  if (
    profile.__typename !== 'ApplicationProfileResponseSuccess' ||
    !profile.result
  ) {
    return [];
  }

  return profile.result.licences.map((licence) =>
    Licence.create({
      id: licence.id,
      description: licence.description,
      expiryDate: licence.expiryDate
        ? MonthYear.create({
            month: licence.expiryDate.month,
            year: licence.expiryDate.year,
          })
        : null,
      issueDate: licence.issueDate
        ? MonthYear.create({
            month: licence.issueDate.month,
            year: licence.issueDate.year,
          })
        : null,
      issuingOrganisation: licence.issuingOrganisation,
      name: ProfileTextObject.create({ text: licence.name }),
      noExpiryDate: licence.noExpiryDate,
      formattedExpiryDate: licence.formattedExpiryDate,
      credential: mapToCredential(licence.credential),
    }),
  );
};

const mapToEducation = (profile: ApplicationFragment['profile']) => {
  if (
    profile.__typename !== 'ApplicationProfileResponseSuccess' ||
    !profile.result
  ) {
    return [];
  }

  return profile.result.education.map((education) =>
    Education.create({
      id: education.id,
      name: ProfileTextObject.create({ text: education.name }),
      completed: education.completed,
      completionDate: education.completionDate
        ? EducationCompletionDate.create({
            year: education.completionDate.year,
            month: education.completionDate.month,
          })
        : null,
      highlights: education.highlights,
      institute: ProfileTextObject.create({ text: education.institute }),
      credential: mapToCredential(education.credential),
    }),
  );
};

const mapToRRStatus = (
  question: ApplicationQuestionnaireQuestion,
): RoleRequirementStatusType => {
  if (question.source === 'Custom') {
    return STATUS_CUSTOM;
  }

  if (question.status === 'MATCH') {
    if (
      question.answers.length > 0 &&
      question.answers.every((a) => a.verification)
    ) {
      return 'CERTIFIED';
    }
    return 'MATCH';
  }

  if (question.status === 'NO_MATCH' || question.status === 'CANNOT_MATCH') {
    return 'NO_MATCH';
  }

  // PARTIAL_MATCH
  return 'NEUTRAL';
};

const mapToRoleRequirements = (
  submission: ApplicationFragment['questionnaireSubmission'],
) => {
  if (
    submission.__typename ===
    'ApplicationQuestionnaireSubmissionResponseSuccess'
  ) {
    if (!submission.result) {
      return {
        hasRRError: false,
        roleRequirement: [],
      };
    }

    return {
      hasRRError: false,
      roleRequirements: submission.result?.questions.map((question) =>
        RoleRequirement.create({
          questionId: question.legacyId,
          globalId: question.id,
          question: question.text,
          rrAnswers: question.answers.map((answer) =>
            RoleReqAnswer.create({
              id: answer.legacyId,
              globalId: answer.id,
              text: answer.text,
              isVerified: Boolean(answer.verification),
            }),
          ),
          answerType: question.answerType,
          status: mapToRRStatus(question),
          verifications: question.answers.reduce<IVerification[]>(
            (verifications, answer) => {
              if (answer.verification) {
                verifications.push(
                  Verification.create({
                    title: answer.verification.title,
                    credentialInfo: answer.verification.viewFields.map(
                      (viewField) => ({
                        name: viewField.label,
                        values: viewField.value,
                      }),
                    ),
                  }),
                );
              }
              return verifications;
            },
            [] as IVerification[],
          ),
        }),
      ),
    };
  }

  return {
    hasRRError: true,
    roleRequirements: [],
  };
};

// this logic comes from here: https://github.com/SEEK-Jobs/cm-graphql-api/blob/4ecd8a1b74531fea18e32406e990d8e200f49882/src/server/schema/prospects/verification/mapper.ts#L22
const mapToVerifications = (
  verifications: ApplicationFragment['verifications'],
): IVerification[] => {
  if (verifications.__typename !== 'ApplicationVerificationsResponseSuccess') {
    return [];
  }

  return verifications.result.map<IVerification>((verification) =>
    Verification.create({
      title: verification.title,
      credentialInfo: verification.viewFields.map((viewField) => ({
        name: viewField.label,
        values: viewField.value,
      })),
    }),
  );
};

const mapToMetadata = (
  metadata: ApplicationFragment['metadata'],
  candidateStats: ApplicationFragment['candidateStats'],
) => {
  if (
    metadata.__typename !== 'ApplicationMetadataResponseSuccess' ||
    candidateStats.__typename !== 'ApplicationCandidateStatsResponseSuccess'
  ) {
    return;
  }

  return {
    prospectEmailStore: ProspectEmailStore.create({
      emailCount: metadata.result.emailCount,
    }),
    notes: Notes.create({ noteCount: metadata.result.noteCount }),
    candidateStore: CandidateStore.create({
      prospectCount: candidateStats.result.totalApplicationCount,
    }),
  };
};

const mapToAttachments = (
  attachments: ApplicationFragment['attachmentsV2'],
) => {
  if (attachments.__typename !== 'ApplicationAttachmentsV2ResponseSuccess') {
    return;
  }

  return {
    attachmentsStore: AttachmentsStore.create({
      attachmentList: attachments.result.map((attachment) => ({
        id: attachment.blobId,
        type: mapGraphAttachmentTypeToStore(attachment.attachmentType),
        isPdf: attachment.hasConvertedPdf,
        filename: attachment.fileName,
      })),
    }),
  };
};

export const mapToBucket = (
  applicationStatus: ApplicationFragment['statusFolder'],
): BUCKETS => {
  switch (applicationStatus) {
    case ApplicationStatusFolder.INBOX:
      return BUCKETS.INBOX;
    case ApplicationStatusFolder.PRESCREEN:
      return BUCKETS.PRESCREEN;
    case ApplicationStatusFolder.SHORTLIST:
      return BUCKETS.SHORTLIST;
    case ApplicationStatusFolder.INTERVIEW:
      return BUCKETS.INTERVIEW;
    case ApplicationStatusFolder.OFFER:
      return BUCKETS.OFFER;
    case ApplicationStatusFolder.ACCEPT:
      return BUCKETS.ACCEPT;
    case ApplicationStatusFolder.NOT_SUITABLE:
      return BUCKETS.NOT_SUITABLE;
    default:
      return BUCKETS.UNKNOWN;
  }
};

const mapToBillableStatus = (
  billableStatus: ApplicationFragment['billableStatus'],
) => {
  switch (billableStatus) {
    case 'BILLABLE':
      return 'BILLABLE';
    case 'NOT_BILLABLE':
      return 'NOT_BILLABLE';
    default:
      return undefined;
  }
};

const mapToVerificationsCount = (profile: ApplicationFragment['profile']) => {
  if (profile.__typename !== 'ApplicationProfileResponseSuccess') {
    return 0;
  }

  if (!profile.result) {
    return 0;
  }

  if (
    profile.result.verificationsStats.__typename !==
    'ApplicationProfileVerificationsStatsSuccess'
  ) {
    return 0;
  }

  if (!profile.result.verificationsStats.totalCount) {
    return 0;
  }

  return profile.result.verificationsStats.totalCount;
};
